import { useEffect, useMemo, useState, useCallback } from "react";
import { Calendar as BigCalendar, dateFnsLocalizer, Views } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import format from "date-fns/format";
import parse from "date-fns/parse";
import startOfWeek from "date-fns/startOfWeek";
import getDay from "date-fns/getDay";
import startOfDay from "date-fns/startOfDay";
import endOfDay from "date-fns/endOfDay";
import endOfWeek from "date-fns/endOfWeek";
import startOfMonth from "date-fns/startOfMonth";
import endOfMonth from "date-fns/endOfMonth";
import { es } from "date-fns/locale/es";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import "@/components/agenda/agenda-calendar.css";

import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { DAY_START_HOUR, DAY_END_HOUR, toLocalIsoString } from "@/lib/agendaConstants";
import AgendaToolbar from "@/components/agenda/AgendaToolbar";
import EventCard from "@/components/agenda/EventCard";
import AppointmentModal from "@/components/agenda/AppointmentModal";
import AppointmentDetailModal from "@/components/agenda/AppointmentDetailModal";
import OnlineBookingsBanner from "@/components/agenda/OnlineBookingsBanner";
import BlockModal from "@/components/agenda/BlockModal";
import { AGENDA } from "@/constants/testIds";

const locales = { es };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });
const DnDCalendar = withDragAndDrop(BigCalendar);

const MESSAGES = {
  allDay: "Todo el día", previous: "Anterior", next: "Siguiente", today: "Hoy",
  month: "Mes", week: "Semana", day: "Día", agenda: "Agenda", date: "Fecha",
  time: "Hora", event: "Turno", noEventsInRange: "No hay turnos en este rango.",
  showMore: (total) => `+${total} mas`,
};

const MIN_TIME = new Date(); MIN_TIME.setHours(DAY_START_HOUR, 0, 0, 0);
const MAX_TIME = new Date(); MAX_TIME.setHours(DAY_END_HOUR, 0, 0, 0);

function getRange(view, date) {
  if (view === "day") return { start: startOfDay(date), end: endOfDay(date) };
  if (view === "week") return { start: startOfWeek(date, { weekStartsOn: 1 }), end: endOfWeek(date, { weekStartsOn: 1 }) };
  return { start: startOfWeek(startOfMonth(date), { weekStartsOn: 1 }), end: endOfWeek(endOfMonth(date), { weekStartsOn: 1 }) };
}

function toEvent(appt) {
  return {
    id: appt.id, title: appt.patient_name, start: new Date(appt.start_time), end: new Date(appt.end_time),
    resourceId: appt.professional_id, status: appt.status, raw: appt,
  };
}

function toBlockEvent(block) {
  return {
    id: `block-${block.id}`, title: block.reason || "Bloqueado", start: new Date(block.start_time), end: new Date(block.end_time),
    resourceId: block.professional_id, isBlock: true, raw: block,
  };
}

export default function AgendaPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const isProfessionalRole = user?.role === "professional";

  // Fix AI (2026-10-23): en el celular la agenda abre en la vista del dia (la semana no entra en la pantalla)
  const [view, setView] = useState(() => (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(max-width: 767px)").matches ? "day" : "week"));
  const [currentDate, setCurrentDate] = useState(new Date());
  const [professionalFilter, setProfessionalFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [professionals, setProfessionals] = useState([]);
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);

  const [apptModalOpen, setApptModalOpen] = useState(false);
  const [apptModalInitial, setApptModalInitial] = useState(null);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [detailAppointment, setDetailAppointment] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [blockModalInitial, setBlockModalInitial] = useState(null);
  const [blockDeleteTarget, setBlockDeleteTarget] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [profsRes, servicesRes, productsRes] = await Promise.all([
          api.get("/team/professionals/list"), api.get("/services"), api.get("/inventory/products"),
        ]);
        setProfessionals(profsRes.data);
        setServices(servicesRes.data);
        setProducts(productsRes.data);
      } catch (err) {
        toast({ title: "Error al cargar equipo/servicios", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const { start, end } = getRange(view, currentDate);
      const params = { start: toLocalIsoString(start), end: toLocalIsoString(end) };
      if (!isProfessionalRole && professionalFilter !== "all") params.professional_id = professionalFilter;
      if (sourceFilter !== "all") params.source = sourceFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      const [apptsRes, blocksRes] = await Promise.all([
        api.get("/appointments", { params }),
        api.get("/schedule-blocks", { params }),
      ]);
      setAppointments(apptsRes.data);
      setBlocks(blocksRes.data);
    } catch (err) {
      toast({ title: "Error al cargar la agenda", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, currentDate, professionalFilter, sourceFilter, statusFilter, isProfessionalRole]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  const events = useMemo(
    () => [...appointments.map(toEvent), ...blocks.map(toBlockEvent)],
    [appointments, blocks]
  );

  const resources = useMemo(() => {
    // Las columnas por profesional (resources) solo tienen sentido en la vista
    // Día: en Semana/Mes, react-big-calendar las combinaria con las columnas
    // de fecha generando una grilla incorrecta.
    if (isProfessionalRole || professionalFilter !== "all" || view !== "day") return undefined;
    return professionals.map((p) => ({ resourceId: p.id, resourceTitle: p.name }));
  }, [isProfessionalRole, professionalFilter, view, professionals]);

  const openCreateModal = (date, professionalId) => {
    const iso = toLocalIsoString(date);
    const [d, t] = iso.split("T");
    setApptModalInitial({ date: d, time: t.slice(0, 5), professionalId: professionalId || "" });
    setEditingAppointment(null);
    setApptModalOpen(true);
  };

  const handleSelectSlot = (slotInfo) => {
    if (view === "month") {
      setCurrentDate(slotInfo.start);
      setView("day");
      return;
    }
    const professionalId = slotInfo.resourceId
      || (isProfessionalRole ? user.professional_id : (professionalFilter !== "all" ? professionalFilter : ""));
    openCreateModal(slotInfo.start, professionalId);
  };

  // Fix AR (2026-10-27): abrir una reserva nueva desde el aviso lleva la agenda a su dia
  const handleOpenOnlineBooking = (appt) => {
    setCurrentDate(new Date(appt.start_time));
    setView("day");
    setDetailAppointment(appt);
    setDetailOpen(true);
  };

  const handleSelectEvent = (event) => {
    if (event.isBlock) { setBlockDeleteTarget(event.raw); return; }
    setDetailAppointment(event.raw);
    setDetailOpen(true);
  };

  const handleEventDrop = async ({ event, start, resourceId }) => {
    if (event.isBlock) return;
    try {
      const payload = { start_time: toLocalIsoString(start) };
      if (resourceId && resourceId !== event.resourceId) payload.professional_id = resourceId;
      await api.put(`/appointments/${event.id}`, payload);
      toast({ title: "Turno reagendado" });
      loadEvents();
    } catch (err) {
      toast({ title: "No se pudo reagendar", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  };

  const handleAppointmentSaved = () => {
    const wasEdit = Boolean(editingAppointment);
    setApptModalOpen(false);
    setEditingAppointment(null);
    setDetailOpen(false);
    toast({ title: wasEdit ? "Turno actualizado" : "Turno creado" });
    loadEvents();
  };

  const handleBlockSaved = () => {
    setBlockModalOpen(false);
    toast({ title: "Horario bloqueado" });
    loadEvents();
  };

  const handleDeleteBlock = async () => {
    try {
      await api.delete(`/schedule-blocks/${blockDeleteTarget.id}`);
      toast({ title: "Bloqueo eliminado" });
      setBlockDeleteTarget(null);
      loadEvents();
    } catch (err) {
      toast({ title: "Error", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  };

  const CalendarToolbar = (toolbarProps) => (
    <AgendaToolbar
      {...toolbarProps}
      professionals={professionals}
      professionalFilter={professionalFilter}
      onProfessionalFilterChange={setProfessionalFilter}
      showProfessionalFilter={!isProfessionalRole}
      sourceFilter={sourceFilter}
      onSourceFilterChange={setSourceFilter}
      statusFilter={statusFilter}
      onStatusFilterChange={setStatusFilter}
      onNewAppointment={() => openCreateModal(new Date(), isProfessionalRole ? user.professional_id : (professionalFilter !== "all" ? professionalFilter : ""))}
      onNewBlock={() => {
        setBlockModalInitial({ professionalId: isProfessionalRole ? user.professional_id : (professionalFilter !== "all" ? professionalFilter : ""), date: currentDate });
        setBlockModalOpen(true);
      }}
    />
  );

  return (
    <div className="space-y-6" data-testid={AGENDA.page}>
      <div>
        <h1 className="font-serif text-3xl sm:text-4xl tracking-tight text-foreground">Agenda</h1>
        <p className="text-base text-muted-foreground mt-1">Gestiona los turnos y la disponibilidad del equipo.</p>
      </div>

      <OnlineBookingsBanner onOpen={handleOpenOnlineBooking} />

      <div className="bg-white border border-border rounded-xl shadow-sm p-3 sm:p-5" data-testid={AGENDA.calendarContainer}>
        {/* Fix AI (2026-10-23): la vista y la cantidad de columnas le dicen al CSS cuanto ancho minimo necesita la grilla en el celular */}
        <div className={`agenda-calendar-wrapper agenda-view-${view} transition-opacity ${loading ? "opacity-60 pointer-events-none" : ""}`} style={{ "--agenda-cols": resources ? resources.length : 1 }}>
          <DnDCalendar
            localizer={localizer}
            culture="es"
            messages={MESSAGES}
            events={events}
            view={view}
            date={currentDate}
            onNavigate={setCurrentDate}
            onView={setView}
            views={["day", "week", "month"]}
            step={30}
            timeslots={1}
            min={MIN_TIME}
            max={MAX_TIME}
            selectable
            resizable={false}
            popup
            resources={resources}
            resourceIdAccessor="resourceId"
            resourceTitleAccessor="resourceTitle"
            draggableAccessor={(event) => !event.isBlock && !["completed", "cancelled"].includes(event.status)}
            onSelectSlot={handleSelectSlot}
            onSelectEvent={handleSelectEvent}
            onEventDrop={handleEventDrop}
            components={{ toolbar: CalendarToolbar, event: EventCard }}
            style={{ height: "calc(100vh - 300px)", minHeight: 560 }}
          />
        </div>
      </div>

      <AppointmentModal
        open={apptModalOpen}
        onOpenChange={setApptModalOpen}
        professionals={professionals}
        services={services}
        initial={apptModalInitial}
        appointment={editingAppointment}
        onSaved={handleAppointmentSaved}
      />

      <AppointmentDetailModal
        open={detailOpen}
        onOpenChange={setDetailOpen}
        appointment={detailAppointment}
        professionals={professionals}
        services={services}
        products={products}
        onChanged={loadEvents}
        onEdit={() => { setEditingAppointment(detailAppointment); setDetailOpen(false); setApptModalOpen(true); }}
      />

      <BlockModal
        open={blockModalOpen}
        onOpenChange={setBlockModalOpen}
        professionals={professionals}
        initial={blockModalInitial}
        onSaved={handleBlockSaved}
      />

      <AlertDialog open={Boolean(blockDeleteTarget)} onOpenChange={(o) => !o && setBlockDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar bloqueo de horario?</AlertDialogTitle>
            <AlertDialogDescription>
              {blockDeleteTarget?.reason ? `"${blockDeleteTarget.reason}"` : "Este bloqueo"} sera eliminado y el horario volvera a estar disponible para agendar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteBlock} className="bg-destructive hover:bg-destructive/90">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
